<template>
  <div class="chat-wrapper d-flex flex-column">
    <!-- Messages / Summary -->
    <div
      ref="scrollArea"
      class="chat-scroll flex-grow-1"
      :class="{ 'summary-scroll': !aiModeChat }"
      @scroll="onScroll"
    >
      <!-- Summary mode: plain panel, no chat bubbles -->
      <template v-if="!aiModeChat">
        <div
          v-if="isStreaming && !streamed"
          class="summary-loading text-muted"
        >
          <div class="typing-dots">
            <span /><span /><span />
          </div>
        </div>
        <vue-markdown
          v-else-if="streamed"
          class="chat-md summary-content"
          :source="streamed"
        />
        <div
          v-else
          class="text-muted small"
        >
          {{ $t('Preparing summary...') }}
        </div>
      </template>

      <!-- Chat mode: existing bubbles -->
      <template v-else>
        <div
          v-for="item in chatItems"
          :key="item._key"
          class="chat-row d-flex"
          :class="item.role === 'user' ? 'justify-content-start' : 'justify-content-end'"
        >
          <div
            class="chat-bubble"
            :class="item.role === 'user' ? 'bubble-user' : 'bubble-assistant'"
          >
            <small class="d-block text-muted mb-1">
              {{ item.role === 'user' ? $t('You') : $t('Assistant') }}
            </small>

            <!-- live updates via :source -->
            <vue-markdown
              class="chat-md"
              :source="item.message"
            />

            <div
              v-if="item.typing"
              class="typing-dots"
            >
              <span /><span /><span />
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- Toolbar -->
    <div class="chat-toolbar">
      <!-- Context Warning -->
      <div
        v-if="contextError"
        class="alert alert-warning alert-sm mb-2 py-2"
      >
        <feather-icon
          icon="AlertTriangleIcon"
          size="14"
        />
        {{ contextError }}
      </div>

      <!-- Chat input only shown in chat mode -->
      <div
        v-if="aiModeChat"
        class="d-flex align-items-start"
      >
        <textarea
          v-model="message"
          class="form-control chat-input"
          :placeholder="$t('Type your message here...')"
          :disabled="isStreaming"
          @keydown.enter.exact.prevent="onEnter"
          @keydown.enter.shift.stop
        />
      </div>
      <div class="chat-footer d-flex align-items-center justify-content-between mt-1">
        <p class="footer-disclaimer small text-muted mb-0 mr-2">
          <feather-icon
            icon="AlertCircleIcon"
            size="12"
          />
          {{ $t('AI disclaimer') }}
        </p>
        <b-link
          v-if="aiModeChat || !isStreaming"
          class="small flex-shrink-0"
          @click="clearChat"
        >
          {{ aiModeChat ? $t('Clear Messages') : $t('Re-run') }}
        </b-link>
      </div>

    </div>
  </div>
</template>

<script>
import useJwt from '@/auth/jwt/useJwt'
import { baseUrl } from '@/libs/axios'
import VueMarkdown from 'vue-markdown'
import { BLink } from 'bootstrap-vue'
import { getUserData, isPermitted } from '@/auth/utils'

export default {
  components: { VueMarkdown, BLink },
  props: { pageId: { type: [Number, String], default: 0 } },
  data() {
    return {
      userData: null,
      ws: null,
      message: '',
      streamed: '',
      isStreaming: false,
      threadId: null,
      messages: [],
      // AI mode: true = multi-turn chat (existing), false = one-shot summary
      aiModeChat: true,
      // For summary mode: holds the AbortController of an in-flight fetch
      summaryAbort: null,
      pageContext: null,
      contextPrepared: false,
      contextError: null,
      firstMessageSent: false,
      // streaming + scroll state
      streamKey: null,
      userPinnedToBottom: true,
      scrollRaf: null,
      // hysteresis thresholds (px)
      pinThreshold: 140,
      unpinThreshold: 260,
      autoAnalyzeDone: false,
      // Re-run debounce timer (summary mode, on filter/data change)
      rerunTimer: null,
      // Context limits
      MAX_CONTEXT_LENGTH: 80000, // 80KB max to stay well under WebSocket limits
      MAX_CONTENT_PER_BOX: 3000, // Max 3KB per box content
    }
  },
  computed: {
    canSend() {
      return this.message && this.message.trim().length > 0
    },
    globalFilterKey() {
      const f = this.$store?.state?.globals?.globalFilter || {}
      // Stringify the filter so the watcher fires on any deep change
      try { return JSON.stringify(f) } catch (_) { return '' }
    },
    chatItems() {
      // Summary mode: never display history list, only the current streaming/last result
      if (!this.aiModeChat) {
        if (this.isStreaming || this.streamed) {
          return [{
            _key: this.streamKey || 'stream-current',
            role: 'assistant',
            message: this.streamed,
            typing: this.isStreaming && !this.streamed,
          }]
        }
        return []
      }

      const base = this.messages.map(m => ({
        _key: `m-${m.id}`,
        role: m.role,
        message: m.message,
      }))

      if (this.isStreaming || this.streamed) {
        base.push({
          _key: this.streamKey || 'stream-current',
          role: 'assistant',
          message: this.streamed,
          typing: this.isStreaming && !this.streamed,
        })
      }
      return base
    },
  },
  watch: {
    async pageId(newVal) {
      if (newVal) {
        console.log('Page changed, resetting context for page:', newVal)
        this.contextPrepared = false
        this.pageContext = null
        this.contextError = null
        this.threadId = null
        this.firstMessageSent = false
        this.autoAnalyzeDone = false
        this.messages = []
        await this.loadChatHistory()
        this.$nextTick(this.forceBottom)
        await this.preparePageContext()
        this.autoAnalyzePageContent()
      }
    },
    globalFilterKey(newVal, oldVal) {
      // Auto re-run summary when page content (filters/period) changes.
      // Chat mode keeps its conversation; only summary mode auto-refreshes.
      if (!this.aiModeChat && newVal && oldVal && newVal !== oldVal) {
        this.scheduleAutoRerun()
      }
    },
  },
  async created() {
    await this.loadAiMode()
    await this.loadChatHistory()
    await this.preparePageContext()
    this.$nextTick(this.forceBottom)
    this.autoAnalyzePageContent()
    this.userData = getUserData()

    // Listen for dashboard box refreshes (filter changes, runbox, drilldowns, etc.)
    // so summary mode can auto re-run once new data is in.
    this.onBoxesRefreshed = () => {
      if (!this.aiModeChat) this.scheduleAutoRerun()
    }
    this.$root.$on('aiBoxesRefreshed', this.onBoxesRefreshed)
  },
  beforeDestroy() {
    if (this.onBoxesRefreshed) {
      this.$root.$off('aiBoxesRefreshed', this.onBoxesRefreshed)
      this.onBoxesRefreshed = null
    }
    if (this.rerunTimer) {
      clearTimeout(this.rerunTimer)
      this.rerunTimer = null
    }
    if (this.summaryAbort) {
      try { this.summaryAbort.abort() } catch (_) { /* ignore */ }
      this.summaryAbort = null
    }
    if (this.ws) {
      try { this.ws.close() } catch (_) { /* ignore */ }
      this.ws = null
    }
  },
  methods: {
    isPermittedLocal() {
      // skip check if pdf export
      if (!this.userData) {
        return false
      }
      return isPermitted(this.userData.role, 'aiChat', this.userData.userGroups)
    },
    findBoxVm(el) {
      // Walk down from the box wrapper to find the Box Vue component instance
      // eslint-disable-next-line no-underscore-dangle
      const vueInstance = el.__vue__
      if (!vueInstance) return null

      const vm = vueInstance
      // Walk up/down the component tree to find the Box instance with items/fields
      if (vm.items !== undefined && vm.fields !== undefined) return vm
      // Check $children
      if (vm.$children) {
        for (let i = 0; i < vm.$children.length; i += 1) {
          const child = vm.$children[i]
          if (child.items !== undefined && child.fields !== undefined) return child
          // One more level deep
          if (child.$children) {
            for (let j = 0; j < child.$children.length; j += 1) {
              const grandchild = child.$children[j]
              if (grandchild.items !== undefined && grandchild.fields !== undefined) return grandchild
            }
          }
        }
      }
      // Check $parent (box_ div might be the Box component's root)
      if (vm.$parent && vm.$parent.items !== undefined && vm.$parent.fields !== undefined) return vm.$parent
      return null
    },
    extractBoxData(vm) {
      const { fields, items, box } = vm
      const type = box?.boxConfig?.type || 'unknown'
      const display = box?.boxConfig?.display || 'chart'
      const lines = []

      lines.push(`Tip: ${type}, Görünüm: ${display}`)

      if (type === 'infographic' || type === 'infographicGroup') {
        // Single value cards
        if (items.length > 0 && items[0].value) {
          lines.push(`Değer: ${items[0].value.val}`)
          if (items[0].base) lines.push(`Baz: ${items[0].base.val}`)
        }
        return lines.join('\n')
      }

      // Build header row
      const headerKeys = fields.map(f => f.key)
      const headerLabels = fields.map(f => f.testLabel || f.key)
      lines.push(`Sütunlar: ${headerLabels.join(' | ')}`)

      // Build data rows
      items.forEach(row => {
        const vals = headerKeys.map(key => {
          if (row[key] && row[key].val !== undefined) {
            return String(row[key].val)
          }
          return ''
        })
        lines.push(vals.join(' | '))
      })

      // Also include series/categories if chart
      if (vm.series && vm.series.length > 0 && vm.categories && vm.categories.length > 0) {
        lines.push(`\nGrafik Kategorileri: ${vm.categories.join(', ')}`)
        vm.series.forEach(s => {
          const dataVals = s.data.map(d => (d && d.y !== undefined ? d.y : d)).join(', ')
          lines.push(`Seri "${s.name}": ${dataVals}`)
        })
      }

      return lines.join('\n')
    },
    autoAnalyzePageContent() {
      // Skip if already has chat history
      if (this.messages.length > 0) return
      // Skip if already auto-analyzed for this page
      if (this.autoAnalyzeDone) return
      // Skip if no page context ready
      if (!this.contextPrepared || !this.pageContext) return
      // Skip if no analysis data (no boxes found)
      if (!this.pageContext.analysis || this.pageContext.analysis.length === 0) return

      this.autoAnalyzeDone = true
      this.message = 'Bu sayfadaki verileri analiz et, önemli bulguları ve trendleri kısaca özetle.'
      this.$nextTick(() => this.start())
    },
    scheduleAutoRerun() {
      // Debounce: filter changes often arrive in bursts (e.g. fromWave + toWave together).
      // Wait a bit, then refresh the page context (which itself polls until boxes finish
      // reloading) and re-run the summary.
      if (this.rerunTimer) clearTimeout(this.rerunTimer)
      this.rerunTimer = setTimeout(async () => {
        this.rerunTimer = null
        if (this.aiModeChat) return

        // Cancel any in-flight summary first
        if (this.summaryAbort) {
          try { this.summaryAbort.abort() } catch (_) { /* ignore */ }
        }

        // Reset context + auto-analyze flag so preparePageContext re-collects fresh data
        // and autoAnalyzePageContent fires again.
        this.streamed = ''
        this.streamKey = null
        this.contextPrepared = false
        this.pageContext = null
        this.contextError = null
        this.autoAnalyzeDone = false
        await this.preparePageContext()
        this.autoAnalyzePageContent()
      }, 800)
    },
    async loadAiMode() {
      try {
        const res = await useJwt.axiosIns.get('settings')
        console.log('res--->', res)
        const raw = res.data?.AiModeChat ?? res.data?.aiModeChat
        if (typeof raw !== 'undefined') {
          this.aiModeChat = raw === true || raw === 'true'
          return
        }
      } catch (error) {
        console.warn('Could not load AiModeChat setting, defaulting to chat mode:', error)
      }

      this.aiModeChat = true

      // check iof user group persmissin is opposite to aiModeChat setting
      if (this.userData && !isPermitted(this.userData.role, 'aiChat', this.userData.userGroups)) {
        this.aiModeChat = false
        console.log('AI mode check', this.aiModeChat)
      }
    },
    async loadChatHistory() {
      if (!this.pageId) return
      // Summary mode: no thread/history persistence
      if (!this.aiModeChat) return

      try {
        // Load thread ID
        const threadResponse = await useJwt.axiosIns.get(`ai/thread/${this.pageId}`)
        if (threadResponse.data && threadResponse.data.threadId) {
          this.threadId = threadResponse.data.threadId
          this.firstMessageSent = true // Already has chat history
        }

        // Load message history
        const messagesResponse = await useJwt.axiosIns.get(`ai/messages/${this.pageId}`)
        if (messagesResponse.data && Array.isArray(messagesResponse.data)) {
          this.messages = messagesResponse.data.map(msg => ({
            id: msg.id,
            role: msg.role,
            message: msg.message,
          }))
          console.log(`Loaded ${this.messages.length} messages for page ${this.pageId}`)
        }
      } catch (error) {
        console.warn('Could not load chat history:', error)
        // Not critical, continue with empty history
      }
    },
    async preparePageContext() {
      if (!this.pageId) return

      this.contextError = null

      try {
        // Wait for boxes to fully load - check multiple times
        let attempts = 0
        const maxAttempts = 20 // 10 seconds max
        let elements = []

        // eslint-disable-next-line no-await-in-loop
        while (attempts < maxAttempts) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise(resolve => setTimeout(resolve, 500))
          elements = document.querySelectorAll('[id^="box_"]')
          // Check if at least one box has finished loading (has Vue instance with items)
          const hasLoaded = Array.from(elements).some(el => {
            const vm = this.findBoxVm(el)
            return vm && !vm.inProgress && vm.items && vm.items.length > 0
          })
          if (elements.length > 0 && hasLoaded) break
          attempts += 1
        }

        console.log(`Found ${elements.length} boxes after ${attempts} attempts`)

        const analysis = []
        elements = document.querySelectorAll('[id^="box_"]')

        elements.forEach(element => {
          try {
            const vm = this.findBoxVm(element)

            // Extract title from DOM
            const titleEl = element.querySelector('.card-title')
            const subtitleEl = element.querySelector('.card-subtitle')
            let title = titleEl?.textContent || ''
            let subtitle = subtitleEl?.textContent || ''
            title = title.trim()
            subtitle = subtitle.trim()

            let contentText = ''

            if (vm && vm.items && vm.items.length > 0 && vm.fields && vm.fields.length > 0) {
              // Extract structured data from Vue component instance
              contentText = this.extractBoxData(vm)
            } else {
              // Fallback: try DOM text content
              const contentEls = element.querySelectorAll('[id^="boxExport_"]')
              contentText = Array.from(contentEls).map(el => el.textContent || '').join(' ').trim()
            }

            // Truncate very long content
            if (contentText.length > this.MAX_CONTENT_PER_BOX) {
              contentText = `${contentText.substring(0, this.MAX_CONTENT_PER_BOX)}... [kısaltıldı]`
            }

            if (contentText
                && contentText.indexOf('Veri bulunamadı') === -1
                && contentText.indexOf('No data') === -1) {
              analysis.push({
                title: `${subtitle}: ${title}`.trim(),
                content: contentText,
              })
            }
          } catch (err) {
            console.warn('Error collecting box content:', err)
          }
        })

        // Get project descriptions
        let projectDescriptions = []
        try {
          const response = await useJwt.axiosIns.get('project/projectdescriptions')
          projectDescriptions = response.data || []
        } catch (err) {
          console.warn('Could not fetch project descriptions:', err)
        }

        // Get global filter
        const globalFilter = this.$store?.state?.globals?.globalFilter || {}
        const period = `${globalFilter.fromWave || ''}-${globalFilter.toWave || ''}`

        // Build system prompt with context
        let systemPrompt = this.buildSystemPrompt(period, analysis, projectDescriptions)

        // Check context size and truncate if necessary
        if (systemPrompt.length > this.MAX_CONTEXT_LENGTH) {
          console.warn(`Context too large: ${systemPrompt.length} chars, truncating to ${this.MAX_CONTEXT_LENGTH}`)
          this.contextError = this.$t('Warning: Page content is very large, some data was truncated')
          systemPrompt = `${systemPrompt.substring(0, this.MAX_CONTEXT_LENGTH)}\n\n[İçerik boyut limiti nedeniyle kısaltıldı]`
        }

        this.pageContext = {
          period,
          analysis,
          projectDescriptions,
          systemPrompt,
        }

        this.contextPrepared = true
        console.log('Page context prepared:', {
          boxCount: analysis.length,
          period,
          projectDescCount: projectDescriptions.length,
          contextLength: systemPrompt.length,
        })
      } catch (error) {
        console.error('Error preparing page context:', error)
        this.contextError = this.$t('Warning: Could not prepare page context')
      }
    },
    buildSystemPrompt(period, analysis, projectDescriptions) {
      const language = this.$i18n?.locale || 'tr'
      const languageInstruction = language === 'tr'
        ? 'Tüm cevaplarını Türkçe olarak ver.'
        : 'Respond in English.'

      let prompt = `Veri analiz asistanı. Dashboard verilerine göre cevapla.

KURALLAR:
- Sadece verilen verileri kullan
- ${languageInstruction}
- Kısa ve öz cevaplar

📊 Dönem: ${period} | Analiz: ${analysis.length}

📈 ANALİZLER:
${analysis.map((a, i) => `\n[${i + 1}] ${a.title}\n${a.content}\n`).join('\n')}
`

      if (projectDescriptions.length > 0) {
        prompt += `\n\n📁 Proje Açıklamaları:\n${JSON.stringify(projectDescriptions, null, 2)}`
      }

      prompt += '\n\nKullanıcı bu dashboard verileri hakkında sorular soracak. Verilen HTML içeriğindeki sayıları, tabloları ve grafik verilerini dikkatlice analiz et. Dönemsel değişiklikleri, trendleri ve anlamlı içgörüleri vurgula.'

      console.log('System prompt prepared:', {
        length: prompt.length,
        analysisCount: analysis.length,
        period,
      })

      return prompt
    },
    onScroll() {
      const el = this.$refs.scrollArea
      if (!el) return
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight

      // hysteresis: avoid rapid toggling near the bottom
      if (this.userPinnedToBottom) {
        if (distanceFromBottom > this.unpinThreshold) this.userPinnedToBottom = false
      } else if (distanceFromBottom <= this.pinThreshold) this.userPinnedToBottom = true
    },
    scrollToBottom({ smoothPreferred = true } = {}) {
      const el = this.$refs.scrollArea
      if (!el || !this.userPinnedToBottom) return

      // During streaming, force 'auto' to avoid queueing smooth animations
      let behavior = 'auto'
      if (this.isStreaming) {
        behavior = 'auto'
      } else if (smoothPreferred) {
        behavior = 'smooth'
      }

      if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf)
      this.scrollRaf = requestAnimationFrame(() => {
        el.scrollTo({ top: el.scrollHeight, behavior })
        this.scrollRaf = null
      })
    },
    forceBottom() {
      this.userPinnedToBottom = true
      this.scrollToBottom({ smoothPreferred: false })
    },
    onEnter() {
      if (this.isStreaming || !this.canSend) return
      this.start()
    },
    async start() {
      if (this.isStreaming || !this.canSend) return

      // Always prepare fresh context before sending to ensure latest data
      console.log('Preparing fresh context before message...')
      await this.preparePageContext()

      this.streamed = ''
      this.isStreaming = true
      this.streamKey = `stream-${Date.now()}`

      const userMessage = this.message.trim()
      const fullMessage = this.pageContext?.systemPrompt
        ? `${this.pageContext.systemPrompt}\n\n${'='.repeat(80)}\n\uD83D\uDCDD KULLANICI SORUSU\n${'='.repeat(80)}\n\n${userMessage}`
        : userMessage

      if (!this.aiModeChat) {
        await this.startSummary(userMessage, fullMessage)
      } else {
        await this.startChat(userMessage, fullMessage)
      }
    },
    async startSummary(userMessage, fullMessage) {
      // One-shot summary via POST /Ai/summarize, no thread/history persistence
      this.message = ''
      const token = localStorage.getItem('accessToken') || ''
      this.summaryAbort = new AbortController()

      try {
        const res = await fetch(`${baseUrl()}/Ai/summarize`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ prompt: fullMessage }),
          signal: this.summaryAbort.signal,
        })

        if (!res.ok || !res.body) {
          const errText = await res.text().catch(() => '')
          throw new Error(errText || `HTTP ${res.status}`)
        }

        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        // eslint-disable-next-line no-constant-condition
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const { value, done } = await reader.read()
          if (done) break
          const chunk = decoder.decode(value, { stream: true })
          if (chunk) {
            this.$set(this, 'streamed', this.streamed + chunk)
            this.scrollToBottom({ smoothPreferred: false })
          }
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('Summary stream aborted')
        } else {
          console.error('\u274C Summary error:', error)
          this.streamed += `\n\n\u274C ${this.$t('Error')}: ${error.message || ''}`
        }
      } finally {
        this.isStreaming = false
        this.streamKey = null
        this.summaryAbort = null
        // Hide the auto-prompt from input but do not store as history (summary mode is stateless)
        // Keep `this.streamed` so the result remains visible until Re-run
        this.scrollToBottom({ smoothPreferred: true })
      }
    },
    async startChat(userMessage, fullMessage) {
      const token = localStorage.getItem('accessToken') || ''

      // WebSocket base URL - use same logic as HTTP API
      let wsBase
      if (process.env.NODE_ENV === 'development') {
        wsBase = process.env.VUE_APP_WS_BASE || 'ws://localhost:8008'
      } else {
        // Production: convert http/https to ws/wss
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
        wsBase = `${protocol}//${window.location.host}/api`
      }

      this.ws = new WebSocket(`${wsBase}/ws/chat?access_token=${encodeURIComponent(token)}`)

      this.ws.onopen = async () => {
        console.log('\u2705 WebSocket connected')

        console.log('\uD83D\uDCE4 Sending message to AI:', {
          userMessageLength: userMessage.length,
          fullMessageLength: fullMessage.length,
          hasContext: !!this.pageContext?.systemPrompt,
          analysisCount: this.pageContext?.analysis?.length || 0,
        })

        try {
          // Send via WebSocket with full context
          this.ws.send(JSON.stringify({
            threadId: this.threadId || undefined,
            message: fullMessage,
          }))
          console.log('\u2705 Message sent successfully')
        } catch (error) {
          console.error('\u274C Error sending message:', error)
          this.isStreaming = false
          this.streamed = `\u274C ${this.$t('Failed to send message. Please try again.')}`
          return
        }

        // Save user message to backend (only the question, not context)
        await useJwt.axiosIns.post('ai/message', {
          pageId: this.pageId,
          role: 'user',
          message: userMessage,
        })

        // Add to local messages (display only user's question)
        this.messages.push({
          id: `temp-${Date.now()}`,
          role: 'user',
          message: userMessage,
        })

        this.message = ''
        this.scrollToBottom({ smoothPreferred: true })
      }

      this.ws.onmessage = async event => {
        const text = event.data

        // Check for special markers first
        if (text === '[[STREAM_DONE]]') {
          console.log('\u2705 Stream completed')
          // Save assistant message to backend
          try {
            await useJwt.axiosIns.post('ai/message', {
              pageId: this.pageId,
              role: 'assistant',
              message: this.streamed,
            })

            this.messages.push({
              id: `asst-${Date.now()}`,
              role: 'assistant',
              message: this.streamed,
            })
          } catch (error) {
            console.error('Error saving assistant message:', error)
            // Still add to local messages even if save fails
            this.messages.push({
              id: `asst-${Date.now()}`,
              role: 'assistant',
              message: this.streamed,
            })
          }
          this.isStreaming = false
          this.streamed = ''
          this.streamKey = null
          this.scrollToBottom({ smoothPreferred: true })
          this.ws.close()
          return
        }

        // Try to parse as JSON for meta/error messages
        try {
          const data = JSON.parse(text)
          if (data.type === 'meta') {
            // Summary mode meta (mode === 'summary' or threadId === null) — ignore thread persistence
            if (data.mode === 'summary' || !data.threadId) {
              console.log('\uD83D\uDCCC Meta: summary mode, no thread to persist')
              return
            }
            this.threadId = data.threadId
            console.log('\uD83D\uDCCC Received thread ID:', this.threadId)
            // Save thread ID to backend
            try {
              await useJwt.axiosIns.post('ai/thread', {
                pageId: this.pageId,
                threadId: this.threadId,
              })
            } catch (error) {
              console.error('Error saving thread ID:', error)
            }
            return
          }
          if (data.type === 'error') {
            console.error('\u274C AI Error:', data.message)
            this.isStreaming = false
            this.streamed += `\n\n\u274C ${this.$t('Error')}: ${data.message}`
            this.ws.close()
            return
          }
          // If it's valid JSON but not meta/error, it might still be content
          // Fall through to append it
        } catch (_) {
          // Not JSON, it's streaming content
        }

        // Append streaming content
        this.$set(this, 'streamed', this.streamed + text)
        this.scrollToBottom({ smoothPreferred: false })
      }

      this.ws.onclose = event => {
        console.log('\uD83D\uDD0C WebSocket closed:', event.code, event.reason)
        this.isStreaming = false
      }

      this.ws.onerror = e => {
        console.error('\u274C WebSocket error:', e)
        this.isStreaming = false
        if (!this.streamed) {
          this.streamed = `\u274C ${this.$t('Connection error. Please try again.')}`
        }
      }
    },
    async clearChat() {
      // Summary mode: just re-run / clear UI; no history endpoints to call
      if (!this.aiModeChat) {
        if (this.summaryAbort) {
          try { this.summaryAbort.abort() } catch (_) { /* ignore */ }
        }
        this.streamed = ''
        this.streamKey = null
        this.contextPrepared = false
        this.pageContext = null
        this.contextError = null
        this.autoAnalyzeDone = false
        // Trigger a fresh auto-analysis (which is the only way summary mode auto-fills the prompt)
        await this.preparePageContext()
        this.autoAnalyzePageContent()
        this.forceBottom()
        return
      }

      if (!this.pageId) return

      try {
        // Delete messages from backend
        await useJwt.axiosIns.delete(`ai/messages/${this.pageId}`)
      } catch (error) {
        console.error('Error deleting messages:', error)
      }

      // Clear local state
      this.messages = []
      this.streamed = ''
      this.threadId = null
      this.firstMessageSent = false
      this.contextPrepared = false
      this.pageContext = null
      this.contextError = null
      console.log('Chat cleared for page:', this.pageId)
      this.forceBottom()
    },
    resetThread() {
      this.threadId = null
      this.firstMessageSent = false
      this.forceBottom()
    },
  },
}
</script>

<style scoped>
/* Layout */
.chat-wrapper {
  min-height: 60vh;
  max-height: 80vh;
}
.chat-scroll {
  overflow-y: auto;
  padding: 1rem;
  background: #f8f9fb;
  border: 1px solid #eee;
  border-radius: .5rem;
  /* Reduce layout shifts */
  overflow-anchor: none;
  overscroll-behavior: contain;
  scrollbar-gutter: stable both-edges;
}

/* Rows & bubbles */
.chat-row { margin: .5rem 0; }
.chat-bubble {
  max-width: 75%;
  padding: .75rem .9rem;
  border-radius: 1rem;
  box-shadow: 0 1px 2px rgba(0,0,0,.06);
  word-break: break-word;
}
.bubble-user {
  background: #0d6efd;
  color: #fff;
  border-top-right-radius: .25rem;
}
.bubble-assistant {
  background: #fff;
  color: #212529;
  border-top-left-radius: .25rem;
  border: 1px solid #eaeaea;
}

/* Markdown inside bubbles */
.chat-md p { margin-bottom: .4rem; }
.chat-md pre { white-space: pre-wrap; }
.chat-md code { word-break: break-word; }
.chat-md img { max-width: 100%; height: auto; display: block; }

/* Input area */
.chat-toolbar { margin-top: .5rem; }
.chat-input { min-height: 72px; resize: vertical; }
.chat-footer .footer-disclaimer { line-height: 1.2; font-size: .72rem; }

/* Summary mode: plain panel, no chat bubbles */
.summary-scroll {
  background: #fff;
}
.summary-content {
  padding: .25rem;
}
.summary-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 80px;
}

/* Alert styling */
.alert-sm {
  font-size: 0.85rem;
  padding: 0.5rem 0.75rem;
}

/* Typing dots */
.typing-dots {
  display: inline-flex;
  gap: 4px;
  margin-top: .25rem;
}
.typing-dots span {
  width: 6px; height: 6px; border-radius: 50%;
  background: currentColor; opacity: .35; display: inline-block;
  animation: blink 1.2s infinite ease-in-out;
}
.typing-dots span:nth-child(2) { animation-delay: .2s; }
.typing-dots span:nth-child(3) { animation-delay: .4s; }

@keyframes blink {
  0%, 80%, 100% { opacity: .35; }
  40% { opacity: 1; }
}
</style>
